/**
 * Role-Based Access Control (RBAC) Utility
 * 
 * Maps API roles to module access permissions based on the access matrix
 */

// Module names matching the matrix
export const MODULES = {
  LOGIN: 'login',
  NEW_LEAD: 'new-lead',
  APPLICATION_FORM: 'application-form',
  DOCUMENT_UPLOAD: 'document-upload',
  CBC_CHECK: 'cbc-check', // Credit Bureau Check module
  HOUSECHECK_VERIFICATION: 'housecheck-verification',
  PRE_APPROVAL: 'pre-approval',
  REJECT_LIST: 'reject-list',
  DASHBOARD: 'dashboard',
  DATA_ENTRY: 'data-entry',
  ADMIN: 'admin',
  REPORT: 'report',
}

// Canonical role names and aliases from API variants
const ROLE_ALIASES = {
  FOE: 'FOE',
  'CBC CHECKER': 'CBC',
  CBC: 'CBC',
  LO: 'LO',
  RM: 'RM',
  'RELATIONSHIP_MANAGER': 'RM',
  'RELATIONSHIP MANAGER': 'RM',
  CRM: 'CRM',
  'CREDIT_MANAGER': 'CRM',
  'CREDIT MANAGER': 'CRM',
  L1: 'CRM',
  HOO: 'HOO',
  ARM: 'RM',
  L2: 'HOO',
  COO: 'COO',
  L3: 'COO',
  BOD: 'BOD',
  L4: 'BOD',
  IT: 'IT',
  SRM: 'RM',
  REM: 'RM',
  CUSTOMER: 'CUSTOMER',
};

const normalizeRole = (role) => {
  const roleUpper = role ? String(role).trim().toUpperCase() : '';
  return ROLE_ALIASES[roleUpper] || roleUpper;
};

// Route to module mapping
export const ROUTE_TO_MODULE = {
  '/login': MODULES.LOGIN,
  '/leads/new': MODULES.NEW_LEAD,
  // Loan Process entry on Dashboard should follow Dashboard (login) access
  '/loan-process': MODULES.DASHBOARD,
  '/client-detail': MODULES.DOCUMENT_UPLOAD,
  '/credit': MODULES.CBC_CHECK, // Credit Bureau Check module
  '/dashboard': MODULES.DASHBOARD,
  '/reports': MODULES.REPORT,
  '/it-admin': MODULES.ADMIN,
}

// Module access matrix based on the provided role-stage image
// Format: { module: [allowedRoles] }
const MODULE_ACCESS_MATRIX = {
  [MODULES.LOGIN]: [
    'FOE', 'CBC', 'LO', 'RM', 'CRM', 'HOO', 'COO', 'BOD', 'IT', 'SRM', 'REM'
    // Note: CUSTOMER is explicitly excluded
  ],
  // FOE - Applicable (Lead, Application Form, House Check)
  [MODULES.NEW_LEAD]: ['FOE', 'LO', 'RM', 'IT'],
  // L1/L2/L3 can view FOE application forms
  [MODULES.APPLICATION_FORM]: ['FOE', 'LO', 'RM', 'CRM', 'HOO', 'COO', 'IT'],
  [MODULES.DOCUMENT_UPLOAD]: ['FOE', 'LO', 'RM', 'IT'],
  // House Check Verification: RM reviews post-CBC; FOE keeps visibility continuity
  [MODULES.HOUSECHECK_VERIFICATION]: ['FOE', 'LO', 'RM', 'IT'],
  // CBC stage: CBC/IT can process, FOE can monitor own applications (view-only enforced in UI)
  [MODULES.CBC_CHECK]: ['FOE', 'CBC', 'IT'],
  // Pre-Approval: CRM(L1), HOO(L1&2), COO(L1&2&3), BOD(4)
  // FOE/LO have view-only visibility continuity for own leads in these stages (enforced in UI)
  [MODULES.PRE_APPROVAL]: ['FOE', 'LO', 'CRM', 'HOO', 'COO', 'BOD', 'IT'],
  [MODULES.REJECT_LIST]: ['FOE', 'LO', 'CRM', 'HOO', 'COO', 'BOD', 'IT'],
  // Dashboard should be accessible to all roles that can login
  [MODULES.DASHBOARD]: ['FOE', 'CBC', 'LO', 'RM', 'CRM', 'HOO', 'COO', 'BOD', 'IT', 'SRM', 'REM'],
  [MODULES.DATA_ENTRY]: ['FOE', 'LO', 'RM', 'IT'],
  [MODULES.ADMIN]: ['IT'],
  [MODULES.REPORT]: ['LO', 'RM', 'CRM', 'HOO', 'COO', 'BOD', 'IT'],
}

/**
 * Get user role from localStorage
 * @returns {string|null} User role or null if not found
 */
export const getUserRole = () => {
  try {
    const userDetail = localStorage.getItem('userDetail');
    if (!userDetail) return null;
    
    const parsed = JSON.parse(userDetail);
    // Role can be in different places: role, roleType, userRole, etc.
    // The API returns role under 'role' key in the token response
    return parsed?.role || parsed?.roleType || parsed?.userRole || parsed?.roleName || null;
  } catch (error) {
    console.error('Error getting user role:', error);
    return null;
  }
}

/**
 * Check if user has access to a specific module
 * @param {string} module - Module name from MODULES constant
 * @param {string} userRole - User role from API (optional, will fetch if not provided)
 * @returns {boolean} True if user has access
 */
export const hasModuleAccess = (module, userRole = null) => {
  const role = userRole || getUserRole();
  
  // Normalize role for comparison (handles aliases)
  const roleUpper = normalizeRole(role);

  // IT has full access to all modules
  if (roleUpper === 'IT') {
    return true;
  }
  
  // CUSTOMER role cannot access any module (including login)
  if (roleUpper === 'CUSTOMER') {
    return false;
  }
  
  // Get allowed roles for this module
  const allowedRoles = MODULE_ACCESS_MATRIX[module] || [];
  const allowedRolesUpper = allowedRoles.map(r => normalizeRole(r));
  
  // If no role provided, allow access only for non-sensitive modules (for backward compatibility)
  // For sensitive modules like CBC_CHECK, ADMIN, etc., require role to be present
  const sensitiveModules = [MODULES.CBC_CHECK, MODULES.ADMIN];
  if (!roleUpper || roleUpper === '' || roleUpper === 'NULL' || roleUpper === 'UNDEFINED') {
    if (sensitiveModules.includes(module)) {
      console.warn(`No role provided for sensitive module "${module}" - denying access`);
      return false;
    }
    console.warn(`No role provided for module "${module}" - allowing access for backward compatibility`);
    return true;
  }
  
  // Case-insensitive comparison - check if role is in allowed list
  const hasAccess = allowedRolesUpper.includes(roleUpper);
  
  console.log(`Module access check for "${module}" with role "${roleUpper}": ${hasAccess ? 'ALLOWED' : 'DENIED'}`);
  if (!hasAccess) {
    console.log(`Allowed roles for "${module}": ${allowedRoles.join(', ')}`);
  }
  
  return hasAccess;
}

/**
 * Check if user can login
 * @param {string} userRole - User role from API (optional, will fetch if not provided)
 * @returns {boolean} True if user can login
 */
export const canLogin = (userRole = null) => {
  // If no role provided, try to get from localStorage
  const role = userRole || getUserRole();
  
  // If still no role, deny login (role must be present)
  if (!role || role === null || role === undefined || role === '') {
    console.warn("No role provided to canLogin - denying login");
    return false;
  }
  
  // Normalize role for comparison (handles aliases)
  const roleStr = normalizeRole(role);
  
  // CUSTOMER role cannot login
  if (roleStr === 'CUSTOMER') {
    console.log(`Login denied for CUSTOMER role`);
    return false;
  }
  
  // Get allowed roles and convert to uppercase for comparison
  const allowedRoles = MODULE_ACCESS_MATRIX[MODULES.LOGIN] || [];
  const allowedRolesUpper = allowedRoles.map(r => normalizeRole(r));
  
  // Check if role is in the allowed login roles (case-insensitive)
  const hasAccess = allowedRolesUpper.includes(roleStr);
  
  console.log(`Login check for role "${roleStr}" (original: "${role}"): ${hasAccess ? 'ALLOWED' : 'DENIED'}`);
  console.log(`Allowed roles: ${allowedRoles.join(', ')}`);
  
  return hasAccess;
}

/**
 * Check if user has access to a route
 * @param {string} route - Route path (e.g., '/dashboard')
 * @param {string} userRole - User role from API (optional, will fetch if not provided)
 * @returns {boolean} True if user has access
 */
export const hasRouteAccess = (route, userRole = null) => {
  const module = ROUTE_TO_MODULE[route];
  if (!module) {
    // If route is not in mapping, allow access (for backward compatibility)
    return true;
  }
  
  // If no role provided, allow access (for backward compatibility)
  if (!userRole || userRole === null || userRole === undefined || userRole === '') {
    console.warn(`No role provided for route "${route}" - allowing access for backward compatibility`);
    return true;
  }
  
  return hasModuleAccess(module, userRole);
}

/**
 * Get all accessible modules for a user
 * @param {string} userRole - User role from API (optional, will fetch if not provided)
 * @returns {string[]} Array of accessible module names
 */
export const getAccessibleModules = (userRole = null) => {
  const role = userRole || getUserRole();
  
  if (!role || normalizeRole(role) === 'CUSTOMER') {
    return [];
  }
  
  return Object.keys(MODULE_ACCESS_MATRIX).filter(module => 
    hasModuleAccess(module, role)
  );
}

/**
 * Get accessible routes for a user
 * @param {string} userRole - User role from API (optional, will fetch if not provided)
 * @returns {string[]} Array of accessible route paths
 */
export const getAccessibleRoutes = (userRole = null) => {
  const accessibleModules = getAccessibleModules(userRole);
  
  return Object.entries(ROUTE_TO_MODULE)
    .filter(([, module]) => accessibleModules.includes(module))
    .map(([route]) => route);
}

